import { z } from 'zod'

// classic prints the original document; packet prints the same records as a
// packet of record sections, through the renderer Canvas packets use.
export const pdfLayoutSchema = z.enum(['classic', 'packet'])

export type PdfLayout = z.infer<typeof pdfLayoutSchema>

const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Dates are YYYY-MM-DD')

export const facesheetPdfBodySchema = z.object({
  scope: z
    .enum(['full', 'medications', 'conditions-and-medications'])
    .optional()
    .nullable(),
  layout: pdfLayoutSchema.optional().nullable(),
})

export const timelinePdfBodySchema = z
  .object({
    startDate: isoDateSchema.optional().nullable(),
    endDate: isoDateSchema.optional().nullable(),
    layout: pdfLayoutSchema.optional().nullable(),
  })
  .refine(
    ({ startDate, endDate }) => !startDate || !endDate || startDate <= endDate,
    { message: 'startDate must be on or before endDate' }
  )

export const recordPdfBodySchema = z.object({
  layout: pdfLayoutSchema.optional().nullable(),
})

// What a caller asks the portal's PDF endpoints for.
export type PdfExportRequest =
  | ({ kind: 'facesheet'; participantId: string } & z.infer<
      typeof facesheetPdfBodySchema
    >)
  | ({ kind: 'timeline'; participantId: string } & z.infer<
      typeof timelinePdfBodySchema
    >)
  | ({ kind: 'order'; orderId: string } & z.infer<typeof recordPdfBodySchema>)
  | ({ kind: 'care-plan'; carePlanId: string } & z.infer<
      typeof recordPdfBodySchema
    >)
  // Also files the PDF on the participant as the order's Order PDF.
  | ({ kind: 'referral'; orderId: string } & z.infer<
      typeof recordPdfBodySchema
    >)
  | { kind: 'appointment-prep'; appointmentId: string }

// The portal serves each export at /api plus this path.
export function pdfExportPath(request: PdfExportRequest): string {
  switch (request.kind) {
    case 'facesheet':
      return `/pdf/facesheet/${request.participantId}`
    case 'timeline':
      return `/pdf/timeline/${request.participantId}`
    case 'order':
      return `/pdf/orders/${request.orderId}`
    case 'care-plan':
      return `/pdf/care-plan/${request.carePlanId}`
    case 'referral':
      return `/pdf/orders/${request.orderId}/referral`
    case 'appointment-prep':
      return `/pdf/appointments/${request.appointmentId}/prep-packet`
  }
}

export interface PdfExport {
  blob: Blob
  filename: string | undefined
}
